/**
 * MutatorPicker — the pre-play rule-variant chooser (Tier 3, Feature H).
 *
 * A DOM card (DOM-over-Pixi, like DebugOverlay / Leaderboard) listing every mutator as a
 * checkbox. Mutators are a PLAY-MODE category, not mid-run toggles — a mutated run is a
 * different leaderboard board — so "Apply" RELOADS the page with the chosen set encoded in
 * `?m=`. Reload (vs in-place world rebuild) routes through the normal boot path, which is
 * deterministic + guarantees the World, ghost, recorder, and board are all rebuilt for the
 * new category in lockstep — and it makes the category SHAREABLE (the `?m=` link carries it,
 * which is exactly what the embeddable cards in I-2 want). `?id` / `?daily` are preserved.
 *
 * Toggle with the M key. Selection-state names render via textContent (XSS-safe).
 */
import './theme.css';
import { MUTATOR_IDS, MUTATORS, type MutatorId, mutatorParam, resolveMutators } from '../engine/Mutators';
import { t } from '../i18n';
import { openModal, type ModalHandle } from './modal';

/** Mutators whose label/blurb indicates a speed-type effect (turbo/fast/dash etc.)
 *  get cyan pill fill instead of purple. */
const SPEED_MUTATORS = new Set<MutatorId>(['turbo'] as MutatorId[]);

export class MutatorPicker {
  private root: HTMLDivElement;
  private visible = false;
  private selected: Set<MutatorId>;
  private catEl!: HTMLDivElement;
  private modal: ModalHandle | null = null;

  /**
   * @param active   the pre-checked set.
   * @param onApply  OPTIONAL. When given (the editor playtest), Apply hands back the chosen
   *                 ids IN PLACE — no reload — so edit state survives. When omitted (the
   *                 game shell), Apply reloads with `?m=` (a clean re-boot + a shareable URL).
   */
  constructor(active: readonly MutatorId[], private onApply?: (ids: MutatorId[]) => void) {
    this.selected = new Set(active);

    this.root = document.createElement('div');
    // sr-panel--modal: cream fill, 4px ink border, 18px radius, modal shadow
    this.root.className = 'sr-panel sr-panel--modal';
    Object.assign(this.root.style, {
      position: 'fixed',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      width: 'min(360px, calc(100vw - 32px))',
      maxHeight: 'calc(100vh - 40px)',
      overflowY: 'auto',
      zIndex: '1500',
      display: 'none',
      padding: '18px',
    } as CSSStyleDeclaration);

    // Title row
    const titleRow = document.createElement('div');
    Object.assign(titleRow.style, {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '4px',
    } as CSSStyleDeclaration);

    const titleIcon = document.createElement('span');
    titleIcon.textContent = '🧬';
    titleIcon.style.fontSize = '18px';

    const titleEl = document.createElement('div');
    titleEl.textContent = t('mutator.title');
    Object.assign(titleEl.style, {
      fontFamily: "'Lilita One', system-ui, sans-serif",
      fontSize: '20px',
      color: '#2b2335',
    } as CSSStyleDeclaration);

    titleRow.append(titleIcon, titleEl);
    this.root.appendChild(titleRow);

    const sub = document.createElement('div');
    sub.textContent = t('mutator.sub');
    Object.assign(sub.style, {
      fontFamily: "'Nunito', system-ui, sans-serif",
      fontSize: '11px',
      color: '#9a93a8',
      marginBottom: '14px',
    } as CSSStyleDeclaration);
    this.root.appendChild(sub);

    // Pill grid for mutators
    const pillGrid = document.createElement('div');
    Object.assign(pillGrid.style, {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      marginBottom: '12px',
    } as CSSStyleDeclaration);

    for (const id of MUTATOR_IDS) {
      pillGrid.appendChild(this.makePill(id));
    }
    this.root.appendChild(pillGrid);

    // Category summary line
    this.catEl = document.createElement('div');
    Object.assign(this.catEl.style, {
      fontFamily: "'Fredoka', system-ui, sans-serif",
      fontWeight: '700',
      fontSize: '12px',
      color: '#a45cff',
      marginBottom: '12px',
      padding: '6px 10px',
      background: '#f5f0ff',
      border: '2px solid #a45cff',
      borderRadius: '10px',
    } as CSSStyleDeclaration);
    this.root.appendChild(this.catEl);

    // Action buttons row
    const actions = document.createElement('div');
    Object.assign(actions.style, {
      display: 'flex',
      gap: '8px',
    } as CSSStyleDeclaration);

    const applyBtn = document.createElement('button');
    applyBtn.type = 'button';
    applyBtn.className = 'sr-btn sr-btn--green';
    applyBtn.style.flex = '1';
    applyBtn.style.fontSize = '13px';
    applyBtn.style.padding = '9px 8px';
    applyBtn.textContent = `▶ ${t('mutator.applyRestart')}`;
    applyBtn.addEventListener('click', () => this.apply());

    const vanillaBtn = document.createElement('button');
    vanillaBtn.type = 'button';
    vanillaBtn.className = 'sr-btn sr-btn--white sr-btn--sm';
    vanillaBtn.textContent = t('mutator.vanilla');
    vanillaBtn.addEventListener('click', () => {
      this.selected.clear();
      this.syncChecks();
      this.apply();
    });

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'sr-btn sr-btn--white sr-btn--sm';
    closeBtn.textContent = t('mutator.close');
    closeBtn.addEventListener('click', () => this.hide());

    actions.append(applyBtn, vanillaBtn, closeBtn);
    this.root.appendChild(actions);

    const hint = document.createElement('div');
    hint.textContent = t('mutator.hint');
    Object.assign(hint.style, {
      fontFamily: "'Fredoka', system-ui, sans-serif",
      fontSize: '11px',
      color: '#b6ad99',
      marginTop: '10px',
      textAlign: 'center',
    } as CSSStyleDeclaration);
    this.root.appendChild(hint);

    document.body.appendChild(this.root);
    this.refreshCategory();
  }

  /** Build a sr-pill for a mutator id (acts as a toggle chip). */
  private makePill(id: MutatorId): HTMLButtonElement {
    const def = MUTATORS[id];
    const isSpeed = SPEED_MUTATORS.has(id);

    const pill = document.createElement('button');
    pill.type = 'button';
    pill.dataset.mut = id;
    pill.title = def.blurb;

    const active = this.selected.has(id);
    pill.className = active
      ? (isSpeed ? 'sr-pill sr-pill--on-cyan' : 'sr-pill sr-pill--on')
      : 'sr-pill';

    pill.textContent = def.label;

    pill.addEventListener('click', () => {
      if (this.selected.has(id)) {
        this.selected.delete(id);
        pill.className = 'sr-pill';
      } else {
        this.selected.add(id);
        pill.className = isSpeed ? 'sr-pill sr-pill--on-cyan' : 'sr-pill sr-pill--on';
      }
      // Keep hidden checkbox data-mut in sync (syncChecks uses input[data-mut])
      this.refreshCategory();
    });

    return pill;
  }

  private syncChecks(): void {
    // Update pill visual state from this.selected
    for (const el of this.root.querySelectorAll<HTMLButtonElement>('button[data-mut]')) {
      const id = el.dataset.mut as MutatorId;
      const isSpeed = SPEED_MUTATORS.has(id);
      const active = this.selected.has(id);
      el.className = active
        ? (isSpeed ? 'sr-pill sr-pill--on-cyan' : 'sr-pill sr-pill--on')
        : 'sr-pill';
    }
  }

  private refreshCategory(): void {
    // resolveMutators().label() is engine-locked (src/engine/Mutators.ts, determinism-bound
    // canonical category string used for leaderboard keys) — only the "Category:" wrapper
    // word is UI chrome and localized here.
    this.catEl.textContent = t('mutator.category', { label: resolveMutators([...this.selected]).label() });
  }

  /** Apply the chosen set: in-place via onApply (editor) or a `?m=` reload (game shell). */
  private apply(): void {
    const ids = [...this.selected];
    if (this.onApply) { this.onApply(ids); this.hide(); return; }
    const params = new URLSearchParams(location.search);
    params.set('m', mutatorParam(ids));
    location.search = params.toString(); // triggers a clean re-boot into the new category
  }

  toggle(): void { this.visible ? this.hide() : this.show(); }
  show(): void {
    this.visible = true; this.syncChecks(); this.refreshCategory(); this.root.style.display = 'block';
    // POLISH_08 #3: dialog semantics + focus trap + Escape-to-close.
    this.modal = openModal(this.root, t('mutator.title'), () => this.hide());
  }
  hide(): void { this.modal?.close(); this.modal = null; this.visible = false; this.root.style.display = 'none'; }
  destroy(): void { this.modal?.close(); this.modal = null; this.root.remove(); }
}
